/**
 * Chat Widget Component
 * Floating chat button and chat window for clients to communicate with support.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, Loader2, MinusCircle, Check, CheckCheck, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { chatService } from '@/services/chatService';
import { useAuth } from '@/contexts/AuthContext';
import { useCall } from '@/components/call';
import { cn } from '@/lib/utils';

export function ChatWidget() {
  const { user } = useAuth();
  const { startCall, callConfig } = useCall();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [typingUser, setTypingUser] = useState(null);
  const [showNewChat, setShowNewChat] = useState(false);
  const [newChatSubject, setNewChatSubject] = useState('');
  const [newChatMessage, setNewChatMessage] = useState('');
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);
  const activeConversationRef = useRef(null);

  // Keep ref in sync with state
  useEffect(() => {
    activeConversationRef.current = activeConversation;
  }, [activeConversation]);

  // Connect WebSocket when user is logged in
  useEffect(() => {
    if (user) {
      const token = localStorage.getItem('access_token');
      if (token) {
        chatService.connect(token);
        loadConversations();
        loadUnreadCount();
      }

      // Set up WebSocket listeners
      const unsubscribe = [
        chatService.on('new_message', handleNewMessage),
        chatService.on('typing', handleTyping),
        chatService.on('notification', handleNotification),
        chatService.on('conversation_closed', handleConversationClosed),
        chatService.on('message_read', handleMessageRead),
        chatService.on('messages_read', handleMessagesRead),
      ];

      return () => {
        unsubscribe.forEach(unsub => unsub());
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
      };
    }
  }, [user]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Subscribe to active conversation and mark messages as read
  useEffect(() => {
    if (activeConversation) {
      chatService.subscribeToConversation(activeConversation.id);
      // Mark all messages as read when opening conversation
      chatService.markAllRead(activeConversation.id).catch(console.error);
      return () => {
        chatService.unsubscribeFromConversation(activeConversation.id);
      };
    }
  }, [activeConversation?.id]);

  const loadConversations = async () => {
    try {
      const data = await chatService.getConversations();
      setConversations(data.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const data = await chatService.getUnreadCount();
      setUnreadCount(data.unread_count || 0);
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  };

  const loadConversation = async (conversationId) => {
    setLoading(true);
    try {
      const data = await chatService.getConversation(conversationId);
      setActiveConversation(data.conversation);
      setMessages(data.messages || []);
      loadUnreadCount();
    } catch (error) {
      console.error('Failed to load conversation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewMessage = (data) => {
    const currentConversation = activeConversationRef.current;
    if (data.conversation_id === currentConversation?.id) {
      // Only add if not already present (avoid duplicates from optimistic update)
      setMessages(prev => {
        const exists = prev.some(msg => msg.id === data.message.id || 
          (msg._optimistic && msg.content === data.message.content));
        if (exists) {
          // Replace optimistic message with real one
          return prev.map(msg => 
            msg._optimistic && msg.content === data.message.content
              ? { ...data.message, _optimistic: false }
              : msg
          );
        }
        return [...prev, data.message];
      });
      // Mark the new message as read since conversation is open
      chatService.markMessageRead(data.conversation_id, data.message.id).catch(console.error);
    }
    loadUnreadCount();
    loadConversations();
  };

  const handleTyping = (data) => {
    const currentConversation = activeConversationRef.current;
    if (data.conversation_id === currentConversation?.id && data.user_id !== user?.id) {
      if (data.is_typing) {
        setTypingUser(data.user_name);
      } else {
        setTypingUser(null);
      }
    }
  };

  const handleNotification = (data) => {
    loadUnreadCount();
  };

  const handleConversationClosed = (data) => {
    const currentConversation = activeConversationRef.current;
    if (data.conversation_id === currentConversation?.id) {
      setActiveConversation(prev => ({ ...prev, status: 'closed' }));
      setMessages(prev => [...prev, data.message]);
    }
    loadConversations();
  };

  const handleMessageRead = (data) => {
    // Update the read status of a single message
    const currentConversation = activeConversationRef.current;
    if (data.conversation_id === currentConversation?.id) {
      setMessages(prev => prev.map(msg => 
        msg.id === data.message_id 
          ? { ...msg, read_at: data.read_at }
          : msg
      ));
    }
  };

  const handleMessagesRead = (data) => {
    // Update the read status of multiple messages
    const currentConversation = activeConversationRef.current;
    if (data.conversation_id === currentConversation?.id) {
      setMessages(prev => prev.map(msg => 
        data.message_ids.includes(msg.id)
          ? { ...msg, read_at: data.read_at }
          : msg
      ));
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConversation) return;

    // Stop typing indicator
    sendTypingStatus(false);

    const messageContent = newMessage.trim();
    setNewMessage('');

    // Create optimistic message
    const optimisticMessage = {
      id: `temp-${Date.now()}`,
      content: messageContent,
      sender_id: user?.id,
      sender_name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'You',
      sender_role: 'client',
      created_at: new Date().toISOString(),
      _optimistic: true
    };

    // Add optimistic message immediately
    setMessages(prev => [...prev, optimisticMessage]);

    setSending(true);
    try {
      const response = await chatService.sendMessage(activeConversation.id, messageContent);
      // Replace optimistic message with real one
      const actualMessage = response.message || response;
      setMessages(prev => prev.map(msg => 
        msg._optimistic && msg.id === optimisticMessage.id 
          ? { ...actualMessage, _optimistic: false }
          : msg
      ));
      inputRef.current?.focus();
    } catch (error) {
      console.error('Failed to send message:', error);
      // Remove optimistic message on error
      setMessages(prev => prev.filter(msg => msg.id !== optimisticMessage.id));
    } finally {
      setSending(false);
    }
  };

  const handleStartNewChat = async (e) => {
    e.preventDefault();
    if (!newChatMessage.trim()) return;

    setSending(true);
    try {
      const data = await chatService.createConversation(
        newChatSubject || 'General Inquiry',
        newChatMessage.trim()
      );
      setShowNewChat(false);
      setNewChatSubject('');
      setNewChatMessage('');
      loadConversations();
      loadConversation(data.conversation.id);
    } catch (error) {
      console.error('Failed to create conversation:', error);
    } finally {
      setSending(false);
    }
  };

  const sendTypingStatus = useCallback((isTyping) => {
    if (activeConversation && isTypingRef.current !== isTyping) {
      isTypingRef.current = isTyping;
      const userName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Client';
      chatService.sendTyping(activeConversation.id, userName, isTyping);
    }
  }, [activeConversation, user]);

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    
    // Send typing indicator
    if (e.target.value.trim()) {
      sendTypingStatus(true);
      
      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Stop typing after 2 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        sendTypingStatus(false);
      }, 2000);
    } else {
      sendTypingStatus(false);
    }
  };

  const handleInputBlur = () => {
    // Stop typing when input loses focus
    sendTypingStatus(false);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  };

  // Read receipt indicator component
  const ReadReceipt = ({ message }) => {
    if (message.sender_id !== user?.id) return null;
    
    return (
      <span className="inline-flex ml-1">
        {message.read_at ? (
          <CheckCheck className="h-3 w-3 text-blue-400" title={`Read at ${new Date(message.read_at).toLocaleString()}`} />
        ) : (
          <Check className="h-3 w-3 opacity-60" title="Delivered" />
        )}
      </span>
    );
  };

  // Don't render if user is not logged in or is agent/admin
  if (!user || ['agent', 'admin', 'agency'].includes(user.role)) {
    return null;
  }

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-primary text-primary-foreground rounded-full shadow-lg flex items-center justify-center hover:bg-primary/90 transition-all hover:scale-105"
          data-testid="chat-widget-button"
        >
          <MessageCircle className="h-6 w-6" />
          {unreadCount > 0 && (
            <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-red-500">
              {unreadCount}
            </Badge>
          )}
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className={cn(
            "fixed bottom-6 right-6 z-50 w-96 bg-background border rounded-lg shadow-2xl flex flex-col transition-all",
            isMinimized ? "h-14" : "h-[500px]"
          )}
          data-testid="chat-widget-window"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b bg-primary text-primary-foreground rounded-t-lg">
            <h3 className="font-semibold">
              {activeConversation ? activeConversation.subject : 'Support Chat'}
            </h3>
            <div className="flex items-center gap-1">
              {/* Call Agent Button */}
              {callConfig?.enabled && activeConversation?.status === 'active' && activeConversation?.agent_id && (
                <button 
                  onClick={() => startCall({
                    conversationId: activeConversation.id,
                    callType: 'video'
                  })}
                  className="p-1 hover:bg-white/20 rounded"
                  title="Call Agent"
                  data-testid="call-agent-btn"
                >
                  <Phone className="h-4 w-4" />
                </button>
              )}
              <button onClick={() => setIsMinimized(!isMinimized)} className="p-1 hover:bg-white/20 rounded">
                <MinusCircle className="h-4 w-4" />
              </button>
              <button onClick={() => { setIsOpen(false); setActiveConversation(null); }} className="p-1 hover:bg-white/20 rounded">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Content */}
              {!activeConversation && !showNewChat ? (
                // Conversation List
                <div className="flex-1 overflow-hidden flex flex-col">
                  <div className="p-3 border-b">
                    <Button onClick={() => setShowNewChat(true)} className="w-full" data-testid="start-new-chat-btn">
                      Start New Chat
                    </Button>
                  </div>
                  <ScrollArea className="flex-1">
                    {conversations.length === 0 ? (
                      <div className="p-4 text-center text-muted-foreground">
                        No conversations yet
                      </div>
                    ) : (
                      <div className="divide-y">
                        {conversations.map(conv => (
                          <button
                            key={conv.id}
                            onClick={() => loadConversation(conv.id)}
                            className="w-full p-3 text-left hover:bg-muted transition-colors"
                            data-testid={`conversation-${conv.id}`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-sm">{conv.subject}</span>
                              {conv.unread_count > 0 && (
                                <Badge variant="destructive" className="text-xs">
                                  {conv.unread_count}
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground truncate mt-1">
                              {conv.last_message || 'No messages'}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={cn(
                                "text-xs px-1.5 py-0.5 rounded",
                                conv.status === 'active' ? "bg-green-100 text-green-700" :
                                conv.status === 'waiting' ? "bg-yellow-100 text-yellow-700" :
                                "bg-gray-100 text-gray-700"
                              )}>
                                {conv.status}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </div>
              ) : showNewChat ? (
                // New Chat Form
                <form onSubmit={handleStartNewChat} className="flex-1 flex flex-col p-4 gap-3">
                  <div>
                    <label className="text-sm font-medium">Subject</label>
                    <Input
                      value={newChatSubject}
                      onChange={(e) => setNewChatSubject(e.target.value)}
                      placeholder="e.g., Tax Filing Question"
                      data-testid="new-chat-subject"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-sm font-medium">Message</label>
                    <textarea
                      value={newChatMessage}
                      onChange={(e) => setNewChatMessage(e.target.value)}
                      placeholder="How can we help you?"
                      className="w-full h-32 p-2 border rounded-md resize-none text-sm"
                      required
                      data-testid="new-chat-message"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => setShowNewChat(false)} className="flex-1">
                      Cancel
                    </Button>
                    <Button type="submit" disabled={sending || !newChatMessage.trim()} className="flex-1" data-testid="submit-new-chat">
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Start Chat'}
                    </Button>
                  </div>
                </form>
              ) : (
                // Messages View
                <>
                  <div className="p-2 border-b">
                    <button
                      onClick={() => setActiveConversation(null)}
                      className="text-sm text-primary hover:underline"
                    >
                      ← Back to conversations
                    </button>
                  </div>
                  
                  <ScrollArea className="flex-1 p-3">
                    {loading ? (
                      <div className="flex items-center justify-center h-full">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {messages.map(msg => (
                          <div
                            key={msg.id}
                            className={cn(
                              "flex",
                              msg.sender_id === user?.id ? "justify-end" : "justify-start"
                            )}
                            data-testid={`message-${msg.id}`}
                          >
                            {msg.message_type === 'system' ? (
                              <div className="text-xs text-muted-foreground text-center w-full py-1">
                                {msg.content}
                              </div>
                            ) : (
                              <div
                                className={cn(
                                  "max-w-[80%] rounded-lg p-2.5 text-sm",
                                  msg.sender_id === user?.id
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-muted"
                                )}
                              >
                                {msg.sender_id !== user?.id && (
                                  <p className="text-xs font-medium mb-1 opacity-70">
                                    {msg.sender_name}
                                  </p>
                                )}
                                <p className="whitespace-pre-wrap">{msg.content}</p>
                                <div className="flex items-center justify-end mt-1">
                                  <p className="text-[10px] opacity-60">
                                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </p>
                                  <ReadReceipt message={msg} />
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                        {typingUser && (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="typing-indicator">
                            <span className="flex gap-1">
                              <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                              <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                              <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                            </span>
                            {typingUser} is typing...
                          </div>
                        )}
                        <div ref={messagesEndRef} />
                      </div>
                    )}
                  </ScrollArea>

                  {/* Message Input */}
                  {activeConversation?.status !== 'closed' ? (
                    <form onSubmit={handleSendMessage} className="p-3 border-t flex gap-2">
                      <Input
                        ref={inputRef}
                        value={newMessage}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        placeholder="Type a message..."
                        disabled={sending}
                        className="flex-1"
                        data-testid="chat-message-input"
                      />
                      <Button type="submit" size="icon" disabled={sending || !newMessage.trim()} data-testid="send-message-btn">
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      </Button>
                    </form>
                  ) : (
                    <div className="p-3 border-t text-center text-sm text-muted-foreground">
                      This conversation has been closed
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}

export default ChatWidget;
